import { useState } from 'react'
import { Link } from 'wouter'

import Button from '@/shared/components/Button'

import TecmaLogo from './TecmaLogo'

const navItems = ['Home', 'Products', 'Projects', 'Professionals', 'About', 'Contact']

type TecmaHeaderProps = {
  activeItem: string
  theme?: 'overlay' | 'light'
}

export default function TecmaHeader({ activeItem, theme = 'overlay' }: TecmaHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const light = theme === 'light'
  const navColor = light ? 'text-[#131a54]' : 'text-white'
  const menuColor = light ? 'border-[#131a54] bg-white text-[#131a54]' : 'border-[#e9ebf6] bg-[#131a54] text-white'

  return (
    <header className={`${light ? 'relative bg-white' : 'absolute bg-transparent'} left-0 right-0 top-0 z-20 flex min-h-16 items-start justify-between px-[4%] pt-2.5 ${navColor} min-[900px]:px-[8.8%]`}>
      <Link href="/" aria-label="Tecma Caribe home" className="shrink-0 focus-visible:outline-3 focus-visible:outline-offset-4 focus-visible:outline-current">
        <TecmaLogo dark={light} />
      </Link>

      <nav aria-label="Main navigation" className="hidden items-center gap-3.5 pt-2.75 text-[16px] font-bold leading-none min-[900px]:flex">
        {navItems.map((item) => {
          const href = item === 'Home' ? '/' : item === 'Products' ? '/products' : '#'

          return (
            <Link key={item} href={href} className={`border-b-2 pb-0.75 pt-0.5 transition-colors hover:border-current ${item === activeItem ? 'border-[#c14a38]' : 'border-transparent'}`}>
              {item}
            </Link>
          )
        })}
        <Button size="compact" className="ml-0.5 translate-y-0.75">Get a Quote</Button>
      </nav>

      <div className="flex items-start gap-2 min-[900px]:hidden">
        <Button size="compact">Quote</Button>
        <button
          type="button"
          aria-controls="mobile-navigation"
          aria-expanded={menuOpen}
          aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          className={`grid h-9.75 w-9.75 place-items-center border-2 ${light ? 'border-[#131a54] bg-white/80' : 'border-white/90 bg-[#131a54]/80'} focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-current`}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="sr-only">Menu</span>
          <span aria-hidden="true" className="flex w-4.5 flex-col gap-1">
            <span className={`h-0.5 w-full ${light ? 'bg-[#131a54]' : 'bg-white'}`} />
            <span className={`h-0.5 w-full ${light ? 'bg-[#131a54]' : 'bg-white'}`} />
            <span className={`h-0.5 w-full ${light ? 'bg-[#131a54]' : 'bg-white'}`} />
          </span>
        </button>
      </div>

      <nav
        id="mobile-navigation"
        aria-label="Mobile navigation"
        className={`${menuOpen ? 'flex' : 'hidden'} absolute right-[4%] top-14.25 w-52.5 flex-col border-2 p-2 shadow-lg ${menuColor} min-[900px]:hidden`}
      >
        {navItems.map((item) => {
          const href = item === 'Home' ? '/' : item === 'Products' ? '/products' : '#'

          return (
            <Link
              key={item}
              href={href}
              className={`border-b px-3 py-3 text-sm font-bold last:border-0 ${light ? 'border-[#131a54]/15 hover:bg-[#e9ebf6]' : 'border-white/20 hover:bg-white/10'}`}
              onClick={() => setMenuOpen(false)}
            >
              {item}
            </Link>
          )
        })}
      </nav>
    </header>
  )
}
